// Research checkout — Stripe Elements (embedded) flow for research-lane
// purchases. Replaces the older "redirect to hosted Stripe Checkout"
// flow because BE switched to client_secret + Elements for full
// VOYA9-branded checkout (matches the consultation-fee page surface).
//
// BE contract (`/app/memory/VOYA9_BE_Research_Catalog_Contract.md` §4):
//   POST /api/consumer/research/checkout
//     body: { items[], finalSaleAckAt, finalSaleAckText, successUrl?, cancelUrl?, discountCode? }
//   response: {
//     clientSecret:      "pi_..._secret_..." (PaymentIntent OR Subscription's invoice PI),
//     mode:              "subscription" | "payment",
//     subscriptionId:    "sub_..." | null,
//     paymentIntentId:   "pi_...",
//     amount:            17400,
//     currency:          "usd",
//   }
//
// Flow:
//   1. Mount → confirm tray has research items, fetch publishable key
//      + POST /consumer/research/checkout with ack fields.
//   2. Render <Elements stripe={...} options={{ clientSecret, appearance }}>.
//   3. Inside, render <PaymentForm /> with <PaymentElement /> + a single
//      "PAY $X →" button.
//   4. Submit calls stripe.confirmPayment with return_url pointing to
//      /app/research/done?pi=<paymentIntentId>. Stripe handles 3DS / SCA.
//   5. /app/research/done clears the research items from the tray and
//      shows confirmation.
//
// IMPORTANT: anonymous visitors who somehow land here are routed back
// to /sign-in?next=/app/research/checkout — the checkout API is auth-
// required.
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import { apiGet, apiPost } from "@/lib/api";
import { useApplicationTray } from "@/lib/applicationTray";
import { useAuth } from "@/lib/auth";
import { C } from "@/lib/brand";
import { fmtMoney } from "@/lib/format";
import { toast } from "@/components/Toast";

// One-time Stripe.js promise cache. Keyed by publishableKey so a
// test↔live swap reloads cleanly.
let stripePromiseCache = { key: null, promise: null };
function getStripePromise(publishableKey) {
  if (!publishableKey) return null;
  if (stripePromiseCache.key !== publishableKey) {
    stripePromiseCache = { key: publishableKey, promise: loadStripe(publishableKey) };
  }
  return stripePromiseCache.promise;
}

// VOYA9 + research-lane appearance for Payment Element. Mirrors the
// consultation-fee surface but with the navy + amber accents that
// signal "research lane" throughout the rest of the page.
const stripeAppearance = {
  theme: "flat",
  variables: {
    colorPrimary: "#0F1B2A",
    colorBackground: "#FFFFFF",
    colorText: "#0F1B2A",
    colorDanger: "#C04E3A",
    fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    fontSizeBase: "16px",
    spacingUnit: "5px",
    borderRadius: "0px",
  },
  rules: {
    ".Label": {
      fontSize: "11px",
      fontWeight: "800",
      letterSpacing: "0.2em",
      textTransform: "uppercase",
      color: "#7E756B",
    },
    ".Input": { border: "1.5px solid #E1DDD7", padding: "16px 18px", transition: "border-color 160ms ease" },
    ".Input:focus": { border: "1.5px solid #0F1B2A", boxShadow: "none" },
    ".Tab": { border: "1.5px solid #E1DDD7", borderRadius: "0" },
    ".Tab--selected": { border: "0", backgroundColor: "#E8C66B", color: "#0F1B2A" },
    ".Tab--selected:hover": { border: "0", backgroundColor: "#E8C66B", color: "#0F1B2A" },
    ".Tab--selected:focus": { border: "0", boxShadow: "none", backgroundColor: "#E8C66B", color: "#0F1B2A" },
  },
};

const NAVY = "#1F3349";
const NAVY_DEEP = "#0F1B2A";
const AMBER = "#E8C66B";

const FINAL_SALE_ACK_TEXT =
  "I understand these are research products and that all sales are final — no returns or refunds.";

export default function ResearchCheckout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { consumer, bootstrapped } = useAuth();
  const tray = useApplicationTray();
  const researchItems = tray.researchItems || [];

  const [publishableKey, setPublishableKey] = useState(null);
  const [stripeMode, setStripeMode] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripeMode2, setStripeMode2] = useState(null); // subscription | payment, from BE
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [amountCents, setAmountCents] = useState(0);
  const [currency, setCurrency] = useState("usd");
  const [ackAt] = useState(() => new Date().toISOString()); // ack captured from the box ticked on Plan tab; we re-stamp here since it's been a fresh hop
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  // Re-entrant path from Stripe redirect-based flows (Klarna, US bank,
  // etc.). When Stripe sends the user back, we land here with ?pi=.
  const returnedPi = searchParams.get("payment_intent");
  const returnedSecret = searchParams.get("payment_intent_client_secret");

  // If we already came back from Stripe with a PI in the URL, hand off
  // directly to /app/research/done.
  useEffect(() => {
    if (returnedPi) {
      navigate(`/app/research/done?pi=${encodeURIComponent(returnedPi)}`, { replace: true });
    }
  }, [returnedPi, navigate]);

  // Send unauthenticated visitors back through sign-in with a next
  // hop that returns them to this page after auth completes. AuthGate
  // higher up would also catch this, but we want a clean redirect
  // rather than a flashed "loading…" screen.
  useEffect(() => {
    if (bootstrapped && !consumer) {
      navigate("/sign-in?next=/app/research/checkout", { replace: true });
    }
  }, [bootstrapped, consumer, navigate]);

  // Guard rail: tray must contain research items. If a user reaches
  // /app/research/checkout directly with an empty research bucket,
  // route them back to the catalogue.
  useEffect(() => {
    if (bootstrapped && consumer && researchItems.length === 0 && !clientSecret && !returnedPi) {
      navigate("/research", { replace: true });
    }
  }, [bootstrapped, consumer, researchItems.length, clientSecret, returnedPi, navigate]);

  useEffect(() => {
    if (returnedPi) return; // already handled
    if (!bootstrapped || !consumer || researchItems.length === 0) return;
    if (clientSecret) return;
    let cancelled = false;
    (async () => {
      try {
        // 1) Public Stripe config.
        const cfg = await apiGet("/stripe/public-config");
        if (cancelled) return;
        if (!cfg?.publishableKey) {
          throw new Error("Stripe is not configured. Please contact support.");
        }
        setPublishableKey(cfg.publishableKey);
        setStripeMode(cfg.mode || (cfg.publishableKey.startsWith("pk_test") ? "test" : "live"));

        // 2) Create the research checkout intent on the BE.
        const payload = {
          items: researchItems.map((it) => ({
            sku: it.sku,
            variant: it.variant || "subscription",
            quantity: it.quantity || 1,
          })),
          finalSaleAckAt: ackAt,
          finalSaleAckText: FINAL_SALE_ACK_TEXT,
          // successUrl / cancelUrl are now optional per BE's new
          // Elements flow but we still send them — they're useful for
          // BE telemetry and any fallback redirect.
          successUrl: `${window.location.origin}/app/research/done?session={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/research`,
        };
        const res = await apiPost("/consumer/research/checkout", payload);
        if (cancelled) return;
        if (!res?.clientSecret) {
          throw new Error("Couldn't initialize payment. Please try again.");
        }
        setClientSecret(res.clientSecret);
        setStripeMode2(res.mode || "payment");
        setSubscriptionId(res.subscriptionId || null);
        setPaymentIntentId(res.paymentIntentId || null);
        setAmountCents(res.amount || 0);
        setCurrency((res.currency || "usd").toUpperCase());
      } catch (err) {
        if (cancelled) return;
        const raw = err?.detail || err?.message || "";
        const isNetwork = /load failed|failed to fetch|network/i.test(raw) || err?.status === 0 || !err?.status;
        // eslint-disable-next-line no-console
        console.warn("[research-checkout] init failed", { status: err?.status, detail: raw, error: err });
        setError({
          kind: isNetwork ? "network" : (err?.status >= 500 ? "server" : "other"),
          message: isNetwork
            ? "We couldn't reach our payment server. Try again — and if it keeps happening, an ad-blocker or VPN may be blocking us."
            : (err?.status >= 500
                ? "Our payment service hiccuped. Please try again in a minute — if it keeps happening we're already on it."
                : (raw || "Couldn't start checkout.")),
        });
      }
    })();
    return () => { cancelled = true; };
  }, [bootstrapped, consumer, researchItems, clientSecret, ackAt, returnedPi, retryNonce]);

  // Subtle order summary that mirrors the navy/amber language of the
  // Plan tab's research section so the user feels continuity.
  const summary = useMemo(() => ({
    items: researchItems.map((it) => ({
      sku: it.sku,
      name: it.name || it.sku,
      ingredient: it.ingredient || "",
      variant: it.variant || "subscription",
      quantity: it.quantity || 1,
      unitCents: (it.variant || "subscription") === "subscription" ? it.unitMonthlyPrice : it.unitOneTimePrice,
    })),
    totalCents: amountCents,
    currency,
  }), [researchItems, amountCents, currency]);

  if (!bootstrapped) {
    return <FullScreen><Spinner /></FullScreen>;
  }
  if (returnedPi) {
    return <FullScreen><Spinner /></FullScreen>;
  }
  if (error) {
    return (
      <FullScreen>
        <Eyebrow color={C.coral}>★ HMM</Eyebrow>
        <Headline before="WE COULDN'T" italic="start checkout." size={48} className="mt-4" />
        <p style={{ marginTop: 18, fontSize: 15, color: C.warmGray, maxWidth: 520, lineHeight: 1.55 }}>
          {error.message}
        </p>
        <div style={{ marginTop: 24, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Button
            variant="primary"
            testId="research-checkout-error-retry"
            onClick={() => { setError(null); setRetryNonce((n) => n + 1); }}
          >
            TRY AGAIN →
          </Button>
          <Button variant="ghost" testId="research-checkout-error-back" onClick={() => navigate("/research")}>
            BACK TO RESEARCH
          </Button>
        </div>
      </FullScreen>
    );
  }
  if (!publishableKey || !clientSecret) {
    return <FullScreen><Spinner /></FullScreen>;
  }

  const stripePromise = getStripePromise(publishableKey);

  return (
    <div data-testid="research-checkout-page" style={{ minHeight: "100vh", background: C.cream, paddingBottom: 80 }}>
      <header style={{ background: NAVY_DEEP, padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Logo light size={20} />
        <span
          style={{
            fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.25em",
            color: AMBER,
            textTransform: "uppercase",
          }}
        >
          ▪ RESEARCH · CHECKOUT
        </span>
      </header>

      <section style={{ padding: "56px 24px 24px", maxWidth: 720, margin: "0 auto" }}>
        <Eyebrow color={NAVY}>★ ORDER SUMMARY</Eyebrow>
        <Headline before="ONE SECURE" italic="payment." color={NAVY_DEEP} italicColor={NAVY} size={56} className="mt-4" />
        <p style={{ marginTop: 16, fontSize: 15, color: C.warmGray, lineHeight: 1.6, maxWidth: 560 }}>
          {stripeMode2 === "subscription"
            ? "Your subscription begins today. Subscriptions renew monthly until cancelled from your account."
            : "Charged once to your card. One-time orders ship within 3 business days."}
          {stripeMode === "test" && " (Test mode — use card 4242 4242 4242 4242.)"}
        </p>
      </section>

      <section style={{ padding: "0 24px", maxWidth: 720, margin: "0 auto" }}>
        <OrderSummary summary={summary} />
      </section>

      <section style={{ padding: "32px 24px 0", maxWidth: 720, margin: "0 auto" }}>
        <Elements
          stripe={stripePromise}
          options={{ clientSecret, appearance: stripeAppearance }}
        >
          <PaymentForm
            paymentIntentId={paymentIntentId}
            subscriptionId={subscriptionId}
            amountCents={amountCents}
            currency={currency}
            stripeMode={stripeMode}
          />
        </Elements>
      </section>
    </div>
  );
}

function OrderSummary({ summary }) {
  const { items, totalCents, currency } = summary;
  return (
    <div
      data-testid="research-checkout-summary-card"
      style={{ background: NAVY_DEEP, color: C.cream, padding: "24px 22px" }}
    >
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
        {items.map((it) => (
          <li
            key={`${it.sku}-${it.variant}`}
            data-testid={`research-checkout-line-${it.sku}`}
            style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.cream, textTransform: "uppercase" }}>
                {it.name}
              </div>
              <div
                style={{
                  fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
                  fontSize: 10,
                  letterSpacing: "0.22em",
                  color: AMBER,
                  textTransform: "uppercase",
                  marginTop: 2,
                }}
              >
                {it.variant === "subscription" ? "▪ SUBSCRIBE" : "▪ ONE-TIME"}
                {it.quantity > 1 && ` · ×${it.quantity}`}
              </div>
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.cream, flexShrink: 0 }}>
              {it.unitCents != null ? fmtMoney((it.unitCents * (it.quantity || 1)) / 100, currency) : "—"}
            </div>
          </li>
        ))}
      </ul>
      <div
        style={{
          marginTop: 18,
          paddingTop: 14,
          borderTop: `1.5px solid rgba(232,198,107,0.25)`,
          display: "flex", justifyContent: "space-between", alignItems: "baseline",
          flexWrap: "wrap", gap: 8,
        }}
      >
        <span
          style={{
            fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
            fontSize: 11, fontWeight: 800, letterSpacing: "0.25em", color: AMBER, textTransform: "uppercase",
          }}
        >
          ▪ TOTAL TODAY
        </span>
        <span data-testid="research-checkout-total" style={{ fontSize: 28, fontWeight: 800, color: C.cream, letterSpacing: "-0.02em" }}>
          {fmtMoney(totalCents / 100, currency)}
        </span>
      </div>
    </div>
  );
}

function PaymentForm({ paymentIntentId, subscriptionId, amountCents, currency, stripeMode }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setFormError(null);

    // Stripe.js handles 3DS / SCA / redirect-based methods inside this
    // call. return_url lands the user on /app/research/done with the
    // PI in the query string — that page reads ?payment_intent= AND
    // clears the research items from the tray.
    const piHint = paymentIntentId || subscriptionId || "";
    const returnUrl = `${window.location.origin}/app/research/done${piHint ? `?pi=${encodeURIComponent(piHint)}` : ""}`;
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: "if_required",
    });

    if (stripeError) {
      setFormError(stripeError.message || "Payment failed. Please try a different card.");
      setSubmitting(false);
      return;
    }

    if (paymentIntent?.status === "succeeded") {
      window.location.href = `${returnUrl}${returnUrl.includes("?") ? "&" : "?"}status=succeeded`;
      return;
    }
    if (paymentIntent?.status === "processing") {
      toast("Payment is processing — we'll email you shortly.", "info");
      window.location.href = returnUrl;
      return;
    }
    setFormError("Payment didn't complete. Please try again.");
    setSubmitting(false);
  };

  return (
    <form onSubmit={submit} data-testid="research-checkout-form">
      <PaymentElement options={{ layout: "tabs" }} />
      {formError && (
        <p data-testid="research-checkout-error" style={{ marginTop: 14, fontSize: 13, color: C.coral, lineHeight: 1.5 }}>
          {formError}
        </p>
      )}
      <div style={{ marginTop: 28 }}>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          testId="research-checkout-submit-btn"
          disabled={!stripe || !elements || submitting}
        >
          {submitting ? "PROCESSING…" : `PAY ${fmtMoney(amountCents / 100, currency)} →`}
        </Button>
      </div>
      <p style={{ marginTop: 14, fontSize: 12, color: C.warmGray, textAlign: "center", lineHeight: 1.5 }}>
        Powered by Stripe. {stripeMode === "test" ? "Test mode — no real charge." : "Your card is charged immediately."}
        {" "}All research-product sales are final.
      </p>
    </form>
  );
}

function FullScreen({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: C.cream, padding: 64, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", textAlign: "center" }}>
      {children}
    </div>
  );
}
